import { useCallback, useEffect, useState } from 'react';

import { Edge, Node, Viewport } from 'reactflow';

import useApi from '@hooks/useApi';
import useAuth from '@hooks/useAuth';

import { CanvasResponse } from '@models/Canvas';

import { ApiError } from '@services/ApiError';
import Endpoints from '@services/Endpoints';

import Logger from '@util/Logger';

import LocalStorageService, { StorageKey } from '../util/LocalStorageService';
import { DEFAULT_ZOOM_LEVEL } from './useCanvasSettings';

const initialViewportValue: Viewport = {
    x: 0,
    y: 0,
    zoom: DEFAULT_ZOOM_LEVEL,
};

const logger = Logger.make('useInitializeCanvas');

type Props = {
    experiment_id?: string;
    isCanvasInUrl: boolean;
    onLoadComplete: (data: {
        nodes: Node[];
        edges: Edge[];
        defaultViewport: Viewport;
        versionId?: string;
        deletedNodes: Node[];
        deletedEdges: Edge[];
    }) => void;
};

const useInitializeCanvas = ({ experiment_id, isCanvasInUrl, onLoadComplete }: Props) => {
    const { authReady, isLoggedIn } = useAuth();
    const [loading, setLoading] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const api = useApi();

    const reset = useCallback(() => {
        setLoading(false);
        setLoaded(false);
        onLoadComplete({
            nodes: [],
            edges: [],
            defaultViewport: initialViewportValue,
            versionId: undefined,
            deletedNodes: [],
            deletedEdges: [],
        });
    }, [onLoadComplete]);

    const fetchCanvas = useCallback(
        async (skipLoadingState = false) => {
            if (!experiment_id) {
                reset();
                return;
            }

            if (!skipLoadingState) {
                setLoaded(false);
                setLoading(true);
            }

            try {
                const fetchResult: CanvasResponse = await api.get(Endpoints.lab.experiment.canvasFlow(experiment_id));

                // Load viewport from localStorage
                const STORAGE_KEY = StorageKey.CANVAS_SETTINGS_EXPERIMENTS;
                const experimentKey = experiment_id || 'global';
                const allParsedSettings = LocalStorageService.getObject<Record<string, any>>(STORAGE_KEY, {}) || {};
                const experimentSettings = allParsedSettings[experimentKey] || {};
                const storedViewport = experimentSettings.viewport || initialViewportValue;

                onLoadComplete({
                    nodes: fetchResult.nodes ?? [],
                    edges: fetchResult.edges ?? [],
                    defaultViewport: storedViewport,
                    versionId: fetchResult.version_id,
                    deletedNodes: fetchResult.deleted_nodes || [],
                    deletedEdges: fetchResult.deleted_edges || [],
                });

                setLoaded(true);
            } catch (error) {
                const errorMessage = ApiError.getMessage(error as Error);
                if (errorMessage === 'Canvasflow data not found.') {
                    await api.post(Endpoints.lab.experiment.canvasFlow(experiment_id), { nodes: [], edges: [] });
                    // Initialize with empty canvas
                    onLoadComplete({
                        nodes: [],
                        edges: [],
                        defaultViewport: initialViewportValue,
                        versionId: undefined,
                        deletedNodes: [],
                        deletedEdges: [],
                    });
                    setLoaded(true);
                } else {
                    logger.error(new Error('Failed to fetch canvas: ' + errorMessage));
                    setLoaded(false);
                }
            } finally {
                setLoading(false);
            }
        },
        [api, experiment_id, onLoadComplete, reset],
    );

    useEffect(() => {
        if (!experiment_id) {
            reset();
            return;
        }
        if (!authReady || !isLoggedIn || !isCanvasInUrl) return;
        fetchCanvas();
    }, [experiment_id, authReady, isCanvasInUrl, isLoggedIn]);
    // do not include [reset, fetchCanvas] in the dependency array to avoid infinite loop

    return {
        isLoaded: loaded,
        isLoading: loading,
        fetchCanvas,
    } as const;
};

export default useInitializeCanvas;
